import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getOrder, sendGuestNotification, type OrderDto } from '../lib/api.js'
import { useSessionStore } from '../stores/session.store.js'
import { formatMoney } from '../lib/money.js'
import { NintaiGOHeader } from '../components/NintaiGOHeader.js'
import { PaymentOnline } from './PaymentOnline.js'

/**
 * OhSir — paso de PAGO. Sirve a DOS flujos:
 *
 *  - TAKEOUT: el comensal armó un pedido PARA LLEVAR. La orden ya está creada
 *    pero NO disparada a cocina — la cocina la recibe sólo cuando el staff
 *    confirma el pago.
 *  - DINE_IN: el comensal ordenó por el QR de MESA y quiere pagar su cuenta
 *    desde la app. La orden ya está en cocina; pagar aquí cierra la mesa.
 *
 * El modo se deriva de `order.type` (no del orderMode de la sesión — la cuenta
 * es la fuente de verdad).
 *
 * UX (decisión del fundador 2026-05-22):
 *  - Métodos de pago disponibles: EFECTIVO y TERMINAL (y nada más).
 *  - En AMBOS casos, las dos acciones son:
 *      1. "Pagar en caja" → preserva la lógica existente:
 *           · DINE_IN  → notificación REQUEST_CHECK al POS + folio en pantalla.
 *           · TAKEOUT  → folio en pantalla; el cajero lo ve en /para-llevar.
 *      2. "Solicitar mesero" → notificación CALL_WAITER con contexto de cobro
 *           (folio + método) para que el mesero vaya a la mesa a cobrar.
 *  - Eliminamos el botón "pagar digital próximamente" (ruido).
 *
 * Sesión compartida (varios comensales en una mesa): se paga la cuenta COMPLETA
 * de la mesa. El split por comensal queda como mejora futura.
 */

type PayMethod = 'CASH' | 'CARD'
type PayAction = 'COUNTER' | 'WAITER'

const METHOD_LABEL: Record<PayMethod, string> = {
  CASH: 'Efectivo',
  CARD: 'Terminal',
}

export function Payment() {
  const { slug, orderId } = useParams<{ slug: string; orderId: string }>()
  const navigate = useNavigate()
  const session = useSessionStore((s) => s.session)
  const [order, setOrder] = useState<OrderDto | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [method, setMethod] = useState<PayMethod | null>(null)
  const [action, setAction] = useState<PayAction | null>(null)
  const [sending, setSending] = useState(false)
  const [notifyError, setNotifyError] = useState<string | null>(null)

  useEffect(() => {
    if (!session) {
      navigate(`/r/${slug}`, { replace: true })
      return
    }
    if (!orderId) return
    let alive = true
    void (async () => {
      const r = await getOrder(session.token, orderId)
      if (!alive) return
      if (!r.ok) setError(`${r.error.code}: ${r.error.message}`)
      else {
        setOrder(r.data)
        setError(null)
      }
    })()
    return () => {
      alive = false
    }
  }, [session, slug, orderId, navigate])

  if (!session) return <></>

  // Dispatch: TAKEOUT + Pago en línea → captura de tarjeta Stripe (PaymentOnline).
  // El resto (efectivo/terminal en caja, cuenta de mesa) usa el flujo manual de abajo.
  if (order?.type === 'TAKEOUT' && order?.chosenPaymentMethod === 'ONLINE') {
    return <PaymentOnline />
  }

  /**
   * Identificador que el comensal da en caja / al mesero. SIEMPRE del servidor:
   *  - para llevar → código de recolección (`XXXX-99999`), que el staff valida;
   *  - en mesa     → folio real de la orden (ORD-000123), que el staff sí puede
   *    buscar en el POS.
   * Antes esto era `orderId.slice(0,8)`: un identificador inventado en el cliente
   * que no correspondía a NADA del lado del negocio (iba hasta en el aviso al
   * mesero). Si la orden todavía no trae ninguno, no inventamos: cadena vacía.
   */
  const folio = order?.pickupCode ?? order?.folio ?? ''
  /** DINE_IN = cuenta de mesa; TAKEOUT = pedido para llevar. */
  const isDineIn = order?.type === 'DINE_IN'
  const alreadyPaid = order?.status === 'CLOSED'

  /**
   * "Pagar en caja" — preserva la lógica que SÍ funciona hoy.
   *  - DINE_IN: avisamos al POS con REQUEST_CHECK (el mesero/cajero va a cobrar
   *    la mesa) y mostramos el folio.
   *  - TAKEOUT: solo mostramos el folio; el cajero lo ve en /para-llevar.
   */
  async function chooseCounter(m: PayMethod): Promise<void> {
    if (!session) return
    setMethod(m)
    setNotifyError(null)
    if (isDineIn) {
      setSending(true)
      const r = await sendGuestNotification(
        session.token,
        session.tableSessionId,
        'REQUEST_CHECK',
        `Pago de mesa solicitado · ${METHOD_LABEL[m]} · folio ${folio}`,
      )
      setSending(false)
      if (!r.ok) {
        // No bloqueamos: igual puede ir a la caja con el folio.
        setNotifyError(
          'No pudimos avisar al staff automáticamente. Acércate a la caja con tu folio.',
        )
      }
    }
    setAction('COUNTER')
  }

  /**
   * "Solicitar mesero" — el comensal pide que el mesero VAYA a la mesa a
   * cobrar. Reutilizamos el sistema de notificaciones categorizadas (ítem B)
   * con el topic `CALL_WAITER` + nota con contexto de cobro (folio + método).
   * El POS muestra la notificación en el panel y el mesero la atiende.
   */
  async function chooseWaiter(m: PayMethod): Promise<void> {
    if (!session) return
    setMethod(m)
    setNotifyError(null)
    setSending(true)
    const note = isDineIn
      ? `Cobro en mesa · ${METHOD_LABEL[m]} · folio ${folio}`
      : `Cobro pedido para llevar · ${METHOD_LABEL[m]} · folio ${folio}`
    const r = await sendGuestNotification(
      session.token,
      session.tableSessionId,
      'CALL_WAITER',
      note,
    )
    setSending(false)
    if (!r.ok) {
      setNotifyError(
        'No pudimos avisar al mesero. Intenta de nuevo o acércate a la caja con tu folio.',
      )
      // Igual mostramos la confirmación con el folio — el comensal no queda atorado.
    }
    setAction('WAITER')
  }

  function resetChoice(): void {
    setMethod(null)
    setAction(null)
    setNotifyError(null)
  }

  const isCounter = action === 'COUNTER'
  const isWaiter = action === 'WAITER'

  return (
    <div className="app">
      <NintaiGOHeader slug={slug} tableCode={session.tableCode} customerName={session.customerName} branchId={session.branchId} />
      <div className="container">
        {error && (
          <div className="card" style={{ borderColor: 'var(--danger)' }}>
            <strong className="danger">No se pudo cargar tu cuenta</strong>
            <div className="muted">{error}</div>
          </div>
        )}

        {/* Resumen de la cuenta / pedido */}
        <div className="card">
          <span className="status-pill">
            {isDineIn ? 'Cuenta de mesa · por pagar' : 'Para llevar · por pagar'}
          </span>
          <h2 style={{ marginTop: 12, marginBottom: 4 }}>
            {isDineIn ? 'Paga la cuenta de tu mesa' : 'Confirma tu pedido'}
          </h2>
          <p className="muted" style={{ marginTop: 0 }}>
            {isDineIn
              ? 'Esta es la cuenta completa de tu mesa. Si son varios, uno paga el total.'
              : 'Tu pedido para llevar entra a cocina en cuanto se confirme el pago.'}
          </p>
          {order && (
            <div style={{ marginTop: 12 }}>
              {order.items.map((it) => (
                <div
                  key={it.id}
                  className="row"
                  style={{ borderTop: '1px solid var(--line)', paddingTop: 10, marginTop: 10 }}
                >
                  <div className="grow">
                    <div style={{ fontWeight: 600 }}>
                      {(it.productName ?? it.name) ?? 'Producto'} × {it.quantity}
                    </div>
                    {it.modifiers && it.modifiers.length > 0 && (
                      <div className="muted" style={{ marginTop: 2 }}>
                        {it.modifiers.map((m) => m.name).join(' · ')}
                      </div>
                    )}
                    {it.notes && <div className="muted">{it.notes}</div>}
                  </div>
                  <div>
                    {formatMoney(
                      (it.unitPriceCents +
                        (it.modifiers ?? []).reduce((s, m) => s + m.priceDeltaCents, 0)) *
                        it.quantity,
                    )}
                  </div>
                </div>
              ))}
              <div
                className="row"
                style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginTop: 12 }}
              >
                <div className="grow"><strong>Total</strong></div>
                <div style={{ color: 'var(--accent-2)', fontWeight: 700, fontSize: 18 }}>
                  {formatMoney(order.totalCents)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Ya pagada */}
        {alreadyPaid ? (
          <div className="card pay-confirm">
            <div className="pay-confirm__eyebrow">¡Gracias por tu visita!</div>
            <p className="pay-confirm__hint">Esta cuenta ya fue pagada y cerrada.</p>
            <div style={{ height: 14 }} />
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/r/${slug}/menu`)}
            >
              Volver al menú
            </button>
          </div>
        ) : isCounter && method ? (
          /* Confirmación: pagar en caja */
          <div className="card pay-confirm">
            <div className="pay-confirm__eyebrow">
              {isDineIn ? 'Pasa a la caja a pagar' : 'Pasa al mostrador a pagar'}
            </div>
            <div className="pay-confirm__method">Método: {METHOD_LABEL[method]}</div>
            <div className="pay-confirm__folio" aria-label="Folio de la cuenta">
              {folio}
            </div>
            <p className="pay-confirm__hint">
              {isDineIn
                ? 'Ya avisamos al staff que quieres pagar. Da este folio en la caja; en cuanto el cajero confirme el pago, tu cuenta queda cerrada.'
                : 'Da este folio en el mostrador y paga tu pedido. En cuanto el cajero confirme el pago, tu orden pasa a cocina y te avisamos cuando esté lista.'}
            </p>
            {notifyError && (
              <p className="pay-confirm__hint pay-confirm__hint--err">{notifyError}</p>
            )}
            <div className="pay-confirm__actions">
              <button className="btn btn-ghost" onClick={resetChoice}>
                ← Cambiar método
              </button>
              <button
                className="btn btn-primary"
                onClick={() =>
                  navigate(
                    isDineIn ? `/r/${slug}/menu` : `/r/${slug}/order/${orderId}`,
                    { replace: true },
                  )
                }
              >
                {isDineIn ? 'Volver al menú' : 'Seguir mi pedido'}
              </button>
            </div>
          </div>
        ) : isWaiter && method ? (
          /* Confirmación: solicitar mesero */
          <div className="card pay-confirm">
            <div className="pay-confirm__eyebrow">El mesero va para allá</div>
            <div className="pay-confirm__method">Método: {METHOD_LABEL[method]}</div>
            <div className="pay-confirm__folio" aria-label="Folio de la cuenta">
              {folio}
            </div>
            <p className="pay-confirm__hint">
              Avisamos al staff que quieres pagar con {METHOD_LABEL[method].toLowerCase()}.
              Un mesero pasará a tu {isDineIn ? 'mesa' : 'pedido'} a cobrar. Si tarda,
              también puedes acercarte a la caja con tu folio.
            </p>
            {notifyError && (
              <p className="pay-confirm__hint pay-confirm__hint--err">{notifyError}</p>
            )}
            <div className="pay-confirm__actions">
              <button className="btn btn-ghost" onClick={resetChoice}>
                ← Cambiar método
              </button>
              <button
                className="btn btn-primary"
                onClick={() =>
                  navigate(
                    isDineIn ? `/r/${slug}/menu` : `/r/${slug}/order/${orderId}`,
                    { replace: true },
                  )
                }
              >
                {isDineIn ? 'Volver al menú' : 'Seguir mi pedido'}
              </button>
            </div>
          </div>
        ) : method ? (
          /* Paso 2: elegir acción para el método ya elegido */
          <div className="card pay-step">
            <div className="pay-step__head">
              <div>
                <div className="pay-step__eyebrow">Paso 2 de 2</div>
                <h3 className="pay-step__title">Vas a pagar con {METHOD_LABEL[method].toLowerCase()}</h3>
              </div>
              <button className="pay-step__back" onClick={resetChoice} aria-label="Cambiar método">
                Cambiar
              </button>
            </div>
            <p className="muted" style={{ marginTop: 0 }}>
              ¿Cómo prefieres hacerlo?
            </p>
            <div className="pay-actions">
              <button
                className="pay-action"
                disabled={sending || !order}
                onClick={() => void chooseCounter(method)}
              >
                <span className="pay-action__icon" aria-hidden>→</span>
                <span className="pay-action__body">
                  <span className="pay-action__label">Pagar en caja</span>
                  <span className="pay-action__hint">
                    {isDineIn
                      ? 'Vas tú a la caja con tu folio. Avisamos al staff.'
                      : 'Pasa al mostrador con tu folio y paga ahí.'}
                  </span>
                </span>
              </button>
              <button
                className="pay-action"
                disabled={sending || !order}
                onClick={() => void chooseWaiter(method)}
              >
                <span className="pay-action__icon" aria-hidden>↩</span>
                <span className="pay-action__body">
                  <span className="pay-action__label">Solicitar mesero</span>
                  <span className="pay-action__hint">
                    El mesero pasa a tu {isDineIn ? 'mesa' : 'pedido'} a cobrar.
                  </span>
                </span>
              </button>
            </div>
            {sending && (
              <p className="muted" style={{ textAlign: 'center', marginTop: 10 }}>
                <span className="spinner" /> Avisando al staff…
              </p>
            )}
          </div>
        ) : (
          /* Paso 1: elegir método */
          <>
            <div className="card pay-step">
              <div className="pay-step__eyebrow">Paso 1 de 2</div>
              <h3 className="pay-step__title">¿Cómo vas a pagar?</h3>
              <p className="muted" style={{ marginTop: 0 }}>
                Elige el método y enseguida te decimos cómo continuar.
              </p>
              <div className="pay-methods">
                <button
                  className="pay-method"
                  disabled={!order}
                  onClick={() => setMethod('CASH')}
                  aria-label="Pagar en efectivo"
                >
                  <span className="pay-method__icon" aria-hidden>$</span>
                  <span className="pay-method__label">Efectivo</span>
                </button>
                <button
                  className="pay-method"
                  disabled={!order}
                  onClick={() => setMethod('CARD')}
                  aria-label="Pagar con terminal"
                >
                  <span className="pay-method__icon" aria-hidden>▢</span>
                  <span className="pay-method__label">Terminal</span>
                </button>
              </div>
            </div>

            <button
              className="btn btn-ghost"
              style={{ marginTop: 12 }}
              onClick={() =>
                navigate(isDineIn ? `/r/${slug}/cuenta` : `/r/${slug}/cart`)
              }
            >
              ← Volver a mi {isDineIn ? 'cuenta' : 'pedido'}
            </button>
          </>
        )}
      </div>
      <div className="footer">
        Powered by Nintai<em>GO</em> — {isDineIn ? 'cuenta de mesa' : 'pedido para llevar'}
      </div>
    </div>
  )
}
